import * as React from 'react'

import { untracked } from 'mobx'
import { observer } from 'mobx-react'

import MapViewSetUpStore from '~/client/src/desktop/views/ProjectSetUp/components/AppsSitemap/MapViewSetUp.store'
import { EquipmentResourceEditBody } from '~/client/src/shared/components/EquipmentResourceEdit/EquipmentResourceEditBody'
import { FormListSelector } from '~/client/src/shared/components/FormListSelector'
import { useResolvedTemplate } from '~/client/src/shared/components/FormListSelector/useResolvedTemplate'
import { FormPropertiesSidebar } from '~/client/src/shared/components/FormPropertiesSidebar'
import { ResourceEdit } from '~/client/src/shared/components/ResourceViewEdit'
import Spinner from '~/client/src/shared/components/Spinner/Spinner'
import { FORBIDDEN_SITE_NAME } from '~/client/src/shared/constants/locationName'
import { ResourceTypeId } from '~/client/src/shared/constants/resourceTypes'
import {
  mergeResourceData,
  splitResourceFormData,
} from '~/client/src/shared/hooks/splitResourceFormData'
import { useLocationResourceStore } from '~/client/src/shared/hooks/useLocationResourceStore'
import { extractControlFields } from '~/client/src/shared/hooks/useResourceControls'
import { useResourceRuntimeContext } from '~/client/src/shared/hooks/useResourceRuntimeContext'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import IGeoPosition from '~/client/src/shared/models/IGeoPosition'
import LocationBase from '~/client/src/shared/models/LocationObjects/LocationBase'
import VerticalObject from '~/client/src/shared/models/LocationObjects/VerticalObject'
import {
  LOCATION_KIND_PROPERTY,
  readStoredKind,
} from '~/client/src/shared/resources/domain/locationKind'
import { toLocationParentInput } from '~/client/src/shared/resources/domain/locationResourceTypeDefinition'

import ProjectSetUpPageStore from '../../views/ProjectSetUp/ProjectSetUpPage.store'
import { buildMapPropertiesSection } from './buildMapPropertiesSection'
import { MapResourceFieldSync } from './MapResourceFieldSync'

interface IProps {
  store: MapViewSetUpStore
  projectSetUpPageStore: ProjectSetUpPageStore
  viewport: IGeoPosition
  saveAlignment: () => void
  exitRubber: () => void
  projectId?: string
}

/**
 * The Location resource card rendered on the Maps setup view in place of
 * PropertiesPanel when the selected map object is a location — for both an existing
 * location (edit) and a freshly-drawn one (create). Record fields save via
 * saveManyLocations; the injected "Map Properties" section reuses PropertiesPanel
 * (bound to the live map store) for the per-map display props.
 *
 * Shares EquipmentResourceEditBody, which is resource-agnostic despite its name - it
 * takes the resource type and its control commands as props. Locations pass no link
 * field: only equipment links to a StruxTag.
 */
const MapLocationResourceCard: React.FC<IProps> = ({
  store,
  projectSetUpPageStore,
  viewport,
  saveAlignment,
  exitRubber,
  projectId,
}) => {
  const runtimeContext = useResourceRuntimeContext()
  const {
    records,
    save,
    schema,
    store: locationStore,
  } = useLocationResourceStore(projectId)

  const dataObjectId =
    store.mapViewItemsSetupStore.selectedMapViewItem?.dataObject?.id
  const recordId = dataObjectId ? String(dataObjectId) : null
  // A drawn-but-unsaved location has no backend id yet, so no resource record
  // exists — render the card in creation mode instead of editing an existing one.
  const isCreating = !recordId

  const [chosenTemplateId, setChosenTemplateId] = React.useState<string | null>(
    null,
  )
  const {
    effectiveTemplateId,
    templates,
    loading: templatesLoading,
  } = useResolvedTemplate({
    enabled: isCreating,
    chosenTemplateId,
    templates: schema.templates,
    loading: schema.loading,
    // Drawing on the map already says what is being created; stopping to pick a form
    // before the card even opens is a step in the way. The default template is used
    // unless one is chosen explicitly, and the picker stays reachable from the card.
    preferDefaultTemplate: true,
  })

  const isAdmin = projectSetUpPageStore.isAdmin
  const [isCreatingNewForm, setIsCreatingNewForm] = React.useState(false)

  // IResourceRecord is structurally a ResourceEditRecord, so it's passed through
  // directly — the card reads it as the form's backing record.
  const record = React.useMemo(
    () => (recordId ? (records.find(r => r.id === recordId) ?? null) : null),
    [records, recordId],
  )

  const drawnItem = store.mapViewItemsSetupStore.selectedMapViewItem

  // Seed the creation form's root fields from the drawn placement so the name and
  // icon (and color/parent) it was drawn with are pre-filled — the name being the
  // one MapViewItemFactory derived from the creation icon ("Crane"), so the form
  // agrees with the label the map is already showing.
  //
  // Keyed on the selected item, not on mount: selection reassigns a fresh copy, so the
  // identity changes exactly when the drawn item does. The card's own React key now falls
  // back to the item's transientKey rather than a shared literal, so a second drawn item
  // gets a new instance anyway - this memo no longer carries that alone.
  //
  // Read untracked: these are seeds, read once per drawn item. Tracked, the card's observer
  // subscribed to the item's icon, colour and name, so the first edit of any of them
  // re-rendered the whole card and its form.
  const creationRootDefaults = React.useMemo(
    () =>
      untracked(() => {
        if (!isCreating) return undefined
        const drawn = drawnItem?.dataObject
        // Seeded as a data default so the Type field is pre-filled the same way the name and icon
        // are, and so it reaches data.type through the ordinary save rather than a special
        // creation path.
        const kind = readStoredKind(drawn?.data)
        return {
          name: drawn?.name ?? null,
          iconName: drawn?.iconName ?? null,
          color: drawn?.color ?? null,
          parent: drawn?.parent ?? null,
          data: kind ? { [LOCATION_KIND_PROPERTY]: kind } : undefined,
        }
      }),
    [isCreating, drawnItem],
  )

  // Close the resource card like any editor: deselect without the map's
  // "Confirm changes?" dialog. The footer Save is the explicit save path; X and
  // Cancel discard uncommitted edits — the same outcome as that dialog's Cancel
  // (hideMapViewItemSaveConfirmDialog also just deselects).
  const handleClose = React.useCallback(() => {
    store.mapViewItemsSetupStore.deselectMapViewItem()
  }, [store])

  // Delete via the map's existing confirmation flow, which soft-deletes the
  // location resource and removes its map placement (rendered by GlobeProperties).
  const handleDelete = React.useCallback(() => {
    const item = store.mapViewItemsSetupStore.selectedMapViewItem
    if (item) store.mapViewItemsSetupStore.showDeleteConfirmationDialog(item)
  }, [store])

  const handleSave = React.useCallback(
    async (formData: Record<string, any>) => {
      // Capture the selected item up front and bail if it no longer matches this
      // card's record (async gap / user reselected another map object), so form
      // data is never applied to or saved onto the wrong item.
      const item = store.mapViewItemsSetupStore.selectedMapViewItem
      if (!item?.dataObject || String(item.dataObject.id) !== recordId) {
        throw new Error('Map selection changed before save')
      }

      const { rootFields, dataFields } = splitResourceFormData(
        ResourceTypeId.LOCATIONS,
        formData,
        ['operatingIntervals', 'permittedCompanies'],
      )
      const location = item.dataObject as LocationBase

      // Validate the incoming name and the item's placement WITHOUT mutating the
      // live map object, so a name the map layer rejects (reserved "Site"), an
      // invalid placement, a failed location mutation, or a selection change
      // never leaves uncommitted edits on the MobX map item.
      const newName =
        typeof rootFields.name === 'string' ? rootFields.name : item.name
      if (
        !newName ||
        newName === FORBIDDEN_SITE_NAME ||
        !item.isValid(store.isGlobeMode)
      ) {
        throw new Error('Invalid map properties')
      }

      // Persist the resource form fields via saveManyLocations. ResourceEdit
      // already normalized formData and only closes the card after onSave
      // resolves, so a throw here keeps the user's edits and the panel open.
      // A vertical object's accessible levels are edited in the map properties, outside
      // the form; a new parent resets them to its building's, as equipment recomputes.
      const newParentId =
        'parent' in rootFields
          ? (toLocationParentInput(rootFields.parent)?.parentId ?? null)
          : null
      const isParentChanged =
        'parent' in rootFields &&
        newParentId !== (location.parent?.parentId ?? null)
      await save(
        recordId,
        location instanceof VerticalObject
          ? {
              ...formData,
              accessibleLevels: isParentChanged
                ? store.mapViewItemsSetupStore.getAccessibleLevelsByParentId(
                    newParentId,
                  )
                : [...location.accessibleLevels],
            }
          : formData,
      )

      // Selection may have changed during the async save — don't persist onto a
      // different item.
      if (store.mapViewItemsSetupStore.selectedMapViewItem !== item) {
        throw new Error('Map selection changed during save')
      }

      // The location mutation has now committed name/color/icon/parent; only at
      // this point sync them onto the live map item for its own save (icon color,
      // per-map display, dataObject). A parent change recomputes accessible
      // levels. Same clear/delete semantics as the resource save.
      item.setName(newName)
      location.data = mergeResourceData(location.data ?? {}, dataFields)
      // The form's single `color` field is the ICON color only — set just that
      // with setColor, leaving the shape fill/line colors (edited independently
      // on the Map tab) untouched. A blanket setAllColors here would repaint the
      // shape and clobber those independent edits (the reported regression).
      if (rootFields.color && rootFields.color !== item.color) {
        item.setColor(rootFields.color)
      }
      if ('iconName' in rootFields) {
        location.iconName = rootFields.iconName ?? location.iconName
      }
      if ('parent' in rootFields) {
        item.setParent(rootFields.parent ?? null)
        if (isParentChanged) {
          store.mapViewItemsSetupStore.resetAccessibleLevels(item)
        }
      }

      // Persist the map object (color/icon/parent/shape + per-map display).
      // Throw on failure so ResourceEdit keeps the card open (matching
      // PropertiesPanel) instead of closing and losing the map-property edits.
      // skipDataObjectSave: the resource + controls mutations above already
      // persisted the location record. Re-saving the location DTO from the map
      // path would write the stale MobX control fields (e.g. permitted companies,
      // operating intervals) back over the just-saved controls — so save only the
      // sitemap placement here.
      const ok = await store.mapViewItemsSetupStore.saveSelectedMapViewItem(
        false,
        item.isDisplayed,
        true,
      )
      if (!ok) {
        throw new Error('Failed to save location map properties')
      }
    },
    [store, save, recordId],
  )

  // Create a freshly-drawn location. All resource fields are applied onto the
  // live (unsaved) map dataObject, then persisted in a SINGLE write via the map
  // save path — which serializes name/icon/color/parent/data/templateId through
  // SaveLocations and writes the new id back onto the map object. Creating via
  // the resource mutation instead would double-write: the MobX map store can't
  // see the Apollo-created record and would create the location again.
  const handleCreate = React.useCallback(
    async (formData: Record<string, any>) => {
      const item = store.mapViewItemsSetupStore.selectedMapViewItem
      // Must still be the unsaved drawn item (no backend id yet).
      if (!item?.dataObject || item.dataObject.id) {
        throw new Error('Map selection changed before create')
      }

      const { templateId, rootFields, dataFields } = splitResourceFormData(
        ResourceTypeId.LOCATIONS,
        formData,
      )
      const location = item.dataObject as LocationBase

      const newName =
        typeof rootFields.name === 'string' ? rootFields.name : item.name
      if (!newName || newName === FORBIDDEN_SITE_NAME) {
        throw new Error('Invalid map properties')
      }

      item.setName(newName)
      // MERGED onto what the palette seeded, not replaced. dataFields carries only the
      // properties the chosen template rendered, so a form with the Type field hidden would
      // otherwise drop the seeded kind - and dataWithKind would then re-derive the bare ROOT
      // from the legacy enum, which the backend refuses as a non-leaf, making an elevator or
      // an information point impossible to draw. mergeResourceData still lets the form win
      // where it does render a field, including clearing one.
      location.data = mergeResourceData(location.data ?? {}, dataFields)
      if (rootFields.color) {
        item.setAllColors(rootFields.color)
      }
      if ('iconName' in rootFields && rootFields.iconName) {
        location.iconName = rootFields.iconName
      }
      if ('parent' in rootFields) {
        item.setParent(rootFields.parent ?? null)
        store.mapViewItemsSetupStore.resetAccessibleLevels(item)
      }
      location.templateId = templateId ?? effectiveTemplateId ?? null

      if (!item.isValid(store.isGlobeMode)) {
        throw new Error('Invalid map properties')
      }

      // shouldKeepSelected: keep the freshly-created location selected so the
      // card stays open in edit mode (see saveSelectedMapViewItem for the fallback).
      const ok = await store.mapViewItemsSetupStore.saveSelectedMapViewItem(
        true,
        item.isDisplayed,
      )
      if (!ok) {
        throw new Error('Failed to create location')
      }
    },
    [store, effectiveTemplateId],
  )

  // Creation: wait for templates, then pick a form when there's more than one.
  if (isCreating && templatesLoading) {
    return (
      <div className="tw:flex tw:items-center tw:justify-center tw:size-full tw:bg-background">
        <Spinner size="md" />
      </div>
    )
  }
  if (isCreating && !effectiveTemplateId) {
    if (isCreatingNewForm) {
      return (
        <FormPropertiesSidebar
          mode="create"
          resourceTypeId={ResourceTypeId.LOCATIONS}
          onClose={handleClose}
          onBackToPicker={() => setIsCreatingNewForm(false)}
          resourceSchema={schema}
          onCreated={newTemplateId => {
            setIsCreatingNewForm(false)
            setChosenTemplateId(newTemplateId)
          }}
        />
      )
    }
    return (
      <FormListSelector
        title={Localization.translator.selectLocationForm}
        titleVariant="panel"
        resourceTypeId={ResourceTypeId.LOCATIONS}
        onSelect={setChosenTemplateId}
        onClose={handleClose}
        onCreateNewForm={isAdmin ? () => setIsCreatingNewForm(true) : undefined}
        templates={templates}
        loading={schema.loading}
      />
    )
  }
  // Edit mode's record comes from the project resource store and can lag a beat
  // behind map selection immediately after creation. Show a spinner rather than
  // a blank panel while the shared location event catches up.
  if (!isCreating && !record) {
    return (
      <div className="tw:flex tw:items-center tw:justify-center tw:size-full tw:bg-background">
        <Spinner size="md" />
      </div>
    )
  }

  const qrCodeUrl = isCreating
    ? null
    : `${window.location.origin}/resource/${ResourceTypeId.LOCATIONS}/${recordId}`

  const mapPropertiesSection = buildMapPropertiesSection({
    store,
    projectSetUpPageStore,
    viewport,
    saveAlignment,
    exitRubber,
  })

  return (
    <>
      <EquipmentResourceEditBody
        recordId={recordId ?? 'new'}
        record={isCreating ? null : record}
        templateId={isCreating ? effectiveTemplateId || undefined : undefined}
        resourceTypeId={ResourceTypeId.LOCATIONS}
        runtimeContext={runtimeContext}
        projectId={projectId}
        qrCodeUrl={qrCodeUrl}
        isCreationMode={isCreating}
        onClose={handleClose}
        onSave={isCreating ? handleCreate : handleSave}
        onTemplateChange={isCreating ? setChosenTemplateId : undefined}
        formSelector={
          isCreating ? (
            <ResourceEdit.FormSelector
              resourceTypeId={ResourceTypeId.LOCATIONS}
              templates={templates}
              currentTemplateId={effectiveTemplateId}
            />
          ) : undefined
        }
        creationRootDefaults={creationRootDefaults}
        isMapsSurface
        keepOpenOnCreate
        onDelete={handleDelete}
        defaultExpandedSectionId="map"
        resourceSchema={schema}
        extraSections={[mapPropertiesSection]}
        formSync={<MapResourceFieldSync store={store} />}
        controlCommands={locationStore.controls}
        controlFields={
          isCreating ? undefined : extractControlFields(record?.root ?? {})
        }
      />
    </>
  )
}

export default observer(MapLocationResourceCard)
